import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Mail, Send } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface EmailTemplate {
  id: string;
  template_name: string;
  template_type: string;
  subject: string;
  html_content: string;
  description: string;
  available_placeholders: string[];
  is_system_template: boolean;
}

interface PreviewEmailTemplateDialogProps {
  template: EmailTemplate | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const getSampleData = (placeholders: string[]): Record<string, string> => {
  const data: Record<string, string> = {};
  
  placeholders.forEach(placeholder => {
    const key = placeholder.replace(/[{}]/g, '');
    
    // Sample data based on placeholder name
    switch (key) {
      case 'user_name':
      case 'member_name':
        data[key] = 'John Smith';
        break;
      case 'user_email':
      case 'email':
        data[key] = 'john.smith@example.com';
        break;
      case 'pta_name':
        data[key] = 'Oakwood PTA';
        break;
      case 'event_name':
        data[key] = 'Summer Fair 2025';
        break;
      case 'event_date':
        data[key] = '15th June 2025';
        break;
      case 'event_time':
        data[key] = '2:00 PM - 5:00 PM';
        break;
      case 'event_location':
        data[key] = 'School Playground';
        break;
      case 'verification_link':
      case 'reset_link':
      case 'magic_link':
      case 'invitation_link':
      case 'rsvp_link':
        data[key] = 'https://ptahub.example.com/verify?token=sample123';
        break;
      case 'login_url':
        data[key] = 'https://ptahub.example.com/login';
        break;
      case 'support_email':
        data[key] = 'support@ptahub.com';
        break;
      case 'expiry_time':
        data[key] = '24 hours';
        break;
      case 'logo_url':
        data[key] = '/logo-full.png';
        break;
      case 'task_title':
        data[key] = 'Organize Bake Sale';
        break;
      case 'total_amount':
      case 'claim_amount':
        data[key] = '£125.00';
        break;
      case 'order_number':
        data[key] = 'ORD-2025-001';
        break;
      case 'status':
        data[key] = 'Approved';
        break;
      default:
        data[key] = `[${key}]`;
    }
  });
  
  return data;
};

const replacePlaceholders = (content: string, data: Record<string, string>): string => {
  let result = content;
  Object.entries(data).forEach(([key, value]) => {
    const placeholder = `{{${key}}}`;
    result = result.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), value);
  });
  return result;
};

export function PreviewEmailTemplateDialog({ template, open, onOpenChange }: PreviewEmailTemplateDialogProps) {
  const { toast } = useToast();
  const [useSampleData, setUseSampleData] = useState(true);
  const [testEmail, setTestEmail] = useState("");
  const [isSending, setIsSending] = useState(false);

  const sampleData = template ? getSampleData(template.available_placeholders) : {};
  const previewSubject = template 
    ? (useSampleData ? replacePlaceholders(template.subject, sampleData) : template.subject)
    : '';
  const previewHtml = template 
    ? (useSampleData ? replacePlaceholders(template.html_content, sampleData) : template.html_content)
    : '';

  const handleSendTest = async () => {
    if (!template) return;
    if (!testEmail || !testEmail.includes('@')) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address",
        variant: "destructive",
      });
      return;
    }

    setIsSending(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error("You must be logged in to send test emails");
      }

      const { data, error } = await supabase.functions.invoke('send-email', {
        body: {
          templateName: template.template_name,
          recipients: testEmail,
          dynamicData: sampleData,
        },
      });

      if (error) throw error;

      toast({
        title: "Test Email Sent!",
        description: `Test email sent successfully to ${testEmail}`,
      });

      setTestEmail("");
    } catch (error: any) {
      console.error("Error sending test email:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to send test email",
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {template && (
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Preview Email Template
            </DialogTitle>
            <DialogDescription>
              {template.template_name.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
            </DialogDescription>
          </DialogHeader>

        <div className="space-y-6">
          <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
            <div className="flex items-center gap-2">
              <Switch
                id="sample-data"
                checked={useSampleData}
                onCheckedChange={setUseSampleData}
              />
              <Label htmlFor="sample-data" className="cursor-pointer">
                Show with sample data
              </Label>
            </div>
            <Badge variant={template.template_type === 'auth' ? 'default' : 'secondary'}>
              {template.template_type}
            </Badge>
          </div>

          <div>
            <Label className="text-sm text-muted-foreground">Subject Line</Label>
            <div className="p-3 bg-muted rounded-md mt-2 font-medium">
              {previewSubject}
            </div>
          </div>

          <div>
            <Label className="text-sm text-muted-foreground mb-2 block">Email Preview</Label>
            <div className="border rounded-lg p-4 bg-background max-h-96 overflow-y-auto">
              <iframe
                srcDoc={previewHtml}
                className="w-full min-h-[400px] border-0"
                title="Email Preview"
                sandbox="allow-same-origin"
              />
            </div>
          </div>

          <div className="border-t pt-6">
            <Label className="text-base font-semibold mb-3 block">Send Test Email</Label>
            <div className="flex gap-2">
              <div className="flex-1">
                <Input
                  type="email"
                  placeholder="Enter test email address"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendTest()}
                />
              </div>
              <Button 
                onClick={handleSendTest} 
                disabled={isSending || !testEmail}
              >
                <Send className="h-4 w-4 mr-2" />
                {isSending ? "Sending..." : "Send Test"}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              A test email will be sent with sample data
            </p>
          </div>
          </div>
        </DialogContent>
      )}
    </Dialog>
  );
}
